// --------------------------------------------------------------
// Created On: 2021-08-06
// Author: Zachary Thomas
//
// Last Modified: 2025-11-04
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import isoToDate from "../../utilities/time/isoToDate";
import formatDateObjectDailyLocal from "../../utilities/time/formatDateObjectDailyLocal";
import formatDateObjectHourlyLocal from "../../utilities/time/formatDateObjectHourlyLocal";
import {
  MS_PER_HOUR,
  GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO,
  GRAPH_MAX_VALUE_DIFF_TO_ENABLE_LARGE_DIFF_ROUNDING,
  GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO_FOR_SMALL_ACCUMULATION,
  GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO_FOR_LARGE_DIFFS,
  GRAPH_HEIGHT_FOR_MOBILE_DEVICES,
  BARELY_BELOW_ZERO_NUMBER,
  BARELY_ABOVE_ZERO_NUMBER,
} from "../../constants/miscellaneous";
import formatDateLabelForMinimumText from "../../utilities/time/formatDateLabelForMinimumText";
import { BREAKPOINT_MEDIUM } from "../../constants/breakpoints";
import getSeriesUnitMap from "../../utilities/graphing/getSeriesUnitMap";
import getSeriesWillDrawYAxis from "../../utilities/graphing/getSeriesWillDrawYAxis";
import getSeriesYAxisColor from "../../utilities/graphing/getSeriesYAxisColor";
import getSeriesThatShareYAxis from "../../utilities/graphing/getSeriesThatShareYAxis";
import getShouldYAxesHaveBorders from "../../utilities/graphing/getShouldYAxesHaveBorders";

// Accumulation chart for viewing data grouped by time slices.
export default function AccumulationChart(props: Props): Component {
  const [series, options] = useMemo(
    () =>
      getFormattedData(
        props.startDate,
        props.endDate,
        props.series,
        props.accumulationType,
        props.axisMode,
        props.height
      ),
    [JSON.stringify(props.series), props.accumulationType, props.startDate, props.endDate, props.axisMode, props.height]
  );

  // Get the structured and formatted data needed to display the accumulation data.
  function getFormattedData(
    startDate: string,
    endDate: string,
    series: VariousTimeDataSeries[],
    accumulationType: AccumulationType,
    axisMode: AxisMode,
    height: number | string
  ): [AccumulationDataSeries[], ApexOptions | null] {
    const [categories, accumulationSeries] = calculateAccumulation(startDate, endDate, accumulationType, series);
    const options = getOptions(accumulationSeries, categories, axisMode, height, accumulationType);
    return [accumulationSeries, options];
  }

  // Get the individual intervals in the x-axis as categories and combine the current data points into these categories.
  function calculateAccumulation(
    startDate: string,
    endDate: string,
    accumulationType: AccumulationType,
    series: VariousTimeDataSeries[]
  ): [string[], AccumulationDataSeries[]] {
    const categories: string[] = [];
    const startDateObject = isoToDate(`${startDate}T00:00:00`);
    const endDateObject = isoToDate(`${endDate}T00:00:00`);
    const dayAfterEndDateObject = isoToDate(`${endDate}T00:00:00`);
    dayAfterEndDateObject.setDate(dayAfterEndDateObject.getDate() + 1);
    const accumulationSeries: AccumulationDataSeries[] = createAccumulationSeries(series);
    // Only calculate accumulation if the start and end dates are valid date objects.
    if (
      startDateObject < dayAfterEndDateObject &&
      startDateObject instanceof Date &&
      dayAfterEndDateObject instanceof Date
    ) {
      const startDateString = startDateObject.toISOString().split("T")[0];
      const currentDateObject = new Date(`${startDateString}T00:00:00`);
      const nextDateObject = new Date(`${startDateString}T00:00:00`);
      switch (accumulationType) {
        case "HOURLY_ACCUMULATION": {
          while (nextDateObject.getTime() < dayAfterEndDateObject.getTime()) {
            currentDateObject.setTime(nextDateObject.getTime());
            nextDateObject.setTime(nextDateObject.getTime() + MS_PER_HOUR);
            let category = String(formatDateObjectHourlyLocal(currentDateObject));
            category = formatDateLabelForMinimumText(startDateObject, endDateObject, "HOUR", category);
            categories.push(category);
            addDataToAccumulationSeries(
              series,
              accumulationSeries,
              currentDateObject,
              nextDateObject,
              startDateObject,
              true
            );
          }
          break;
        }
        case "DAILY_ACCUMULATION": {
          while (nextDateObject.getTime() < dayAfterEndDateObject.getTime()) {
            currentDateObject.setTime(nextDateObject.getTime());
            nextDateObject.setDate(nextDateObject.getDate() + 1);
            let category = String(formatDateObjectDailyLocal(currentDateObject));
            category = formatDateLabelForMinimumText(startDateObject, endDateObject, "DAY", category);
            categories.push(category);
            addDataToAccumulationSeries(
              series,
              accumulationSeries,
              currentDateObject,
              nextDateObject,
              startDateObject,
              true
            );
          }
          break;
        }
        case "WEEKLY_ACCUMULATION": {
          // We break out the response into data chunked by the calendar weeks while keeping within start/end date bounds.
          while (nextDateObject.getTime() < dayAfterEndDateObject.getTime()) {
            currentDateObject.setTime(nextDateObject.getTime());
            nextDateObject.setTime(getStartOfNextWeek(nextDateObject, dayAfterEndDateObject).getTime());
            // We need to get the date before the next date to show on the x-axis label since the next date is actually
            // the start of the next week.
            const finalDayObject = new Date();
            finalDayObject.setTime(nextDateObject.getTime());
            finalDayObject.setDate(finalDayObject.getDate() - 1);
            let category = `${formatDateObjectDailyLocal(currentDateObject)} - ${formatDateObjectDailyLocal(
              finalDayObject
            )}`;
            category = formatDateLabelForMinimumText(startDateObject, endDateObject, "WEEK", category);
            categories.push(category);
            addDataToAccumulationSeries(
              series,
              accumulationSeries,
              currentDateObject,
              nextDateObject,
              startDateObject,
              true
            );
          }
          break;
        }
        case "MONTHLY_ACCUMULATION": {
          // We break out the response into data chunked by the calendar months while keeping within start/end date bounds.
          while (nextDateObject.getTime() < dayAfterEndDateObject.getTime()) {
            currentDateObject.setTime(nextDateObject.getTime());
            nextDateObject.setTime(getStartOfNextMonth(nextDateObject, dayAfterEndDateObject).getTime());
            // We need to get the date before the next date to show on the x-axis label since the next date is actually
            // the start of the next month.
            const finalDayObject = new Date();
            finalDayObject.setTime(nextDateObject.getTime());
            finalDayObject.setDate(finalDayObject.getDate() - 1);
            categories.push(
              `${formatDateObjectDailyLocal(currentDateObject)} - ${formatDateObjectDailyLocal(finalDayObject)}`
            );
            addDataToAccumulationSeries(
              series,
              accumulationSeries,
              currentDateObject,
              nextDateObject,
              startDateObject,
              true
            );
          }
          break;
        }
        default:
          break;
      }
    }
    return [categories, accumulationSeries];
  }

  // Given a date, get the next day that is the first day of a week.
  function getStartOfNextWeek(currentDateObject: Date, dayAfterEndDateObject: Date): Date {
    // The standard JS getDay method returns the day of the week represented by an integer (0 to 6).
    const sunday = 0;
    const startOfNextWeek = new Date();
    startOfNextWeek.setTime(currentDateObject.getTime());
    startOfNextWeek.setDate(startOfNextWeek.getDate() + 1);
    // We increment the next day until we either find a Sunday or reach the last date in our date range.
    while (startOfNextWeek.getDay() !== sunday && startOfNextWeek.getTime() < dayAfterEndDateObject.getTime()) {
      startOfNextWeek.setDate(startOfNextWeek.getDate() + 1);
    }
    return startOfNextWeek;
  }

  // Given a date, get the next day that is the first day of a month.
  function getStartOfNextMonth(currentDateObject: Date, dayAfterEndDateObject: Date): Date {
    const currentMonth = currentDateObject.getMonth();
    const startOfNextMonth = new Date();
    startOfNextMonth.setTime(currentDateObject.getTime());
    startOfNextMonth.setDate(startOfNextMonth.getDate() + 1);
    // We increment the next day until we either find the first day of the next month
    // or we reach the last date in our date range.
    while (
      startOfNextMonth.getMonth() === currentMonth &&
      startOfNextMonth.getTime() < dayAfterEndDateObject.getTime()
    ) {
      startOfNextMonth.setDate(startOfNextMonth.getDate() + 1);
    }
    return startOfNextMonth;
  }

  // Given a various time data series, convert it to a column data series without any data.
  function createAccumulationSeries(series: VariousTimeDataSeries[]): AccumulationDataSeries[] {
    const accumulationSeries: AccumulationDataSeries[] = [];
    series.forEach((individualSeries) => {
      accumulationSeries.push({
        name: individualSeries.name,
        unitName: individualSeries.unitName,
        unitShortName: individualSeries.unitShortName,
        data: [],
      });
    });
    return accumulationSeries;
  }

  // Attempt to populate each data series within a given start and end date with accumulated data.
  function addDataToAccumulationSeries(
    series: VariousTimeDataSeries[],
    accumulationSeries: AccumulationDataSeries[],
    currentDateObject: Date,
    nextStartDateObject: Date,
    startDateObject: Date,
    showData: boolean
  ): void {
    series.forEach((individualSeries, i) => {
      // We will get the oldest and newest values from our time data series that match the time frame.
      let newestTimestamp: number | null = null;
      let oldestTimestamp: number | null = null;
      let newestValue: number | null = null;
      let oldestValue: number | null = null;
      individualSeries.data.forEach((dataPoint) => {
        if (dataPoint.length === 2) {
          const dataPointTimestamp = dataPoint[0];
          let dataPointValue = 0;
          if (showData) {
            dataPointValue = parseFloat(dataPoint[1] as string);
          }
          const currentDateTimestamp = currentDateObject.getTime();
          const nextStartDateTimestamp = nextStartDateObject.getTime();
          if (dataPointTimestamp >= currentDateTimestamp && dataPointTimestamp < nextStartDateTimestamp) {
            if (newestTimestamp === null || dataPointTimestamp > newestTimestamp) {
              newestTimestamp = dataPointTimestamp;
              newestValue = dataPointValue;
            }
            if (oldestTimestamp === null || dataPointTimestamp < oldestTimestamp) {
              oldestTimestamp = dataPointTimestamp;
              oldestValue = dataPointValue;
            }
          }
        }
      });

      let accumulatedValue: number;
      if (oldestTimestamp === null || oldestValue === null || newestTimestamp === null || newestValue === null) {
        // If we didn't find any data in our current time slice, then we assume no new accumulation.
        accumulatedValue = 0;
      } else {
        // So that we don't lose value changes between time slices, we need to get the difference between the oldest value
        // that we have found and the previous reported value in the current date range that we have selected.
        // Example: [0 to 5][10 to 10]. If you just check the difference in the time slices you will have [5] and [0]
        // for a total of 5 accumulated, but we should expect to see total of 10 accumulated if we also check between time slices.
        let previousTimestamp: number | null = null;
        let previousValue: number = oldestValue;
        individualSeries.data.forEach((dataPoint) => {
          if (dataPoint.length === 2) {
            const dataPointTimestamp = dataPoint[0];
            const dataPointValue = parseFloat(dataPoint[1] as string);
            if (
              oldestTimestamp !== null &&
              dataPointTimestamp < oldestTimestamp &&
              dataPointTimestamp >= startDateObject.getTime() &&
              (previousTimestamp === null || dataPointTimestamp > previousTimestamp)
            ) {
              previousTimestamp = dataPointTimestamp;
              previousValue = dataPointValue;
            }
          }
        });
        accumulatedValue = newestValue - previousValue;
      }

      // We will use the matching indexes of the accumulation series to populate the accumulated data.
      if (accumulationSeries.length > i) {
        accumulationSeries[i].data.push(accumulatedValue);
      }
    });
  }

  // Get the options needed to configure the column chart.
  function getOptions(
    series: AccumulationDataSeries[],
    categories: string[],
    axisMode: AxisMode,
    height: number | string,
    accumulationType: AccumulationType
  ): ApexOptions | null {
    const yAxes: ApexYAxis[] = [];
    const responsiveYAxes: ApexYAxis[] = [];
    const seriesUnitMap = getSeriesUnitMap(series);
    const yAxesHaveBorders = getShouldYAxesHaveBorders(series, axisMode);
    const seriesWithMaxValues = addFixedMaxValuesToSeries(series, accumulationType);

    seriesWithMaxValues.forEach((singleSeries, i) => {
      const currentSeriesDrawsYAxis = getSeriesWillDrawYAxis(i, singleSeries.unitName, seriesUnitMap, axisMode);
      const seriesColor = getSeriesYAxisColor(
        i,
        singleSeries.unitName,
        seriesUnitMap,
        axisMode,
        seriesWithMaxValues.length
      );
      const seriesThatShareYAxis = getSeriesThatShareYAxis(
        i,
        singleSeries.unitName,
        seriesUnitMap,
        axisMode,
        seriesWithMaxValues
      );
      const maxValue = verticalBorderMax(seriesThatShareYAxis);
      const minValue = verticalBorderMin(seriesThatShareYAxis);

      yAxes.push({
        show: currentSeriesDrawsYAxis,
        seriesName: singleSeries.name,
        opposite: i % 2 !== 0,
        showAlways: axisMode === "Y_AXIS_UOM",
        tickAmount: 4,
        max: maxValue,
        min: minValue,
        labels: {
          formatter: (value) => {
            let decimalPlaceToRoundTo = GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO;
            if (maxValue - minValue > GRAPH_MAX_VALUE_DIFF_TO_ENABLE_LARGE_DIFF_ROUNDING) {
              decimalPlaceToRoundTo = GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO_FOR_LARGE_DIFFS;
            }
            let roundedValue: number;
            if (typeof value === "string") {
              roundedValue = parseFloat(parseFloat(value).toFixed(decimalPlaceToRoundTo));
            } else {
              roundedValue = parseFloat(value.toFixed(decimalPlaceToRoundTo));
            }
            return `${roundedValue} ${singleSeries.unitShortName === null ? "" : singleSeries.unitShortName}`;
          },
          style: {
            colors: seriesColor,
          },
        },
        axisBorder: {
          show: yAxesHaveBorders,
          color: seriesColor,
        },
      });

      responsiveYAxes.push({
        show: currentSeriesDrawsYAxis,
        seriesName: singleSeries.name,
        opposite: i % 2 !== 0,
        showAlways: axisMode === "Y_AXIS_UOM",
        tickAmount: 1,
        max: maxValue,
        min: minValue,
        floating: true,
        labels: {
          offsetX: 75,
          formatter: (value) => {
            let decimalPlaceToRoundTo = GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO_FOR_SMALL_ACCUMULATION;
            if (maxValue - minValue > GRAPH_MAX_VALUE_DIFF_TO_ENABLE_LARGE_DIFF_ROUNDING) {
              decimalPlaceToRoundTo = GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO_FOR_LARGE_DIFFS;
            }
            let roundedValue: number;
            if (typeof value === "string") {
              roundedValue = parseFloat(parseFloat(value).toFixed(decimalPlaceToRoundTo));
            } else {
              roundedValue = parseFloat(value.toFixed(decimalPlaceToRoundTo));
            }
            return `${roundedValue} ${singleSeries.unitShortName === null ? "" : singleSeries.unitShortName}`;
          },
          style: {
            colors: seriesColor,
          },
        },
        axisBorder: {
          show: false,
          color: seriesColor,
        },
      });
    });

    // Set general options.
    if (seriesWithMaxValues !== null && seriesWithMaxValues.length > 0) {
      return {
        chart: {
          height: height,
          type: "bar",
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
        },
        grid: {
          row: {
            colors: ["var(--chart-background)", "var(--chart-background-alternative)"],
            opacity: 1,
          },
          padding: {
            left: 5,
            right: 0,
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
        },
        xaxis: {
          categories: categories,
          offsetY: 9,
        },
        yaxis: yAxes,
        legend: {
          onItemClick: {
            toggleDataSeries: false,
          },
        },
        responsive: [
          {
            breakpoint: BREAKPOINT_MEDIUM,
            options: {
              chart: {
                height: GRAPH_HEIGHT_FOR_MOBILE_DEVICES,
                type: "bar",
                zoom: {
                  enabled: false,
                },
                toolbar: {
                  show: false,
                },
              },
              legend: {
                offsetY: 6,
              },
              yaxis: responsiveYAxes,
              grid: {
                row: {
                  colors: ["var(--chart-background)", "var(--chart-background-alternative)"],
                  opacity: 1,
                },
                padding: {
                  left: -100,
                  right: -100,
                },
              },
            },
          },
        ],
      };
    } else {
      return null;
    }
  }

  // Takes in a list of series, and based on the accumulation mode, decides what max y-axis label should be shown.
  // Updates the array of individual series that are passed in to include a new maxValue field.
  // A null maxValue represents a series that does not have a fixed max y-axis label (is calculated dynamically).
  // An integer maxValue represents the max y-axis label that will show up on the graph for the series.
  function addFixedMaxValuesToSeries(
    series: AccumulationDataSeries[],
    accumulationType: AccumulationType
  ): AccumulationDataSeriesWithMaxValue[] {
    const seriesWithMaxValues: AccumulationDataSeriesWithMaxValue[] = [];
    // This map acts as an alternative to a switch statement for value look ups.
    const maxValueMap = {
      HOURS: {
        HOURLY_ACCUMULATION: 1,
        DAILY_ACCUMULATION: 24,
        WEEKLY_ACCUMULATION: 168,
        MONTHLY_ACCUMULATION: 744,
      },
      MINUTES: {
        HOURLY_ACCUMULATION: 1 * 60,
        DAILY_ACCUMULATION: 24 * 60,
        WEEKLY_ACCUMULATION: 168 * 60,
        MONTHLY_ACCUMULATION: 744 * 60,
      },
      SECONDS: {
        HOURLY_ACCUMULATION: 1 * 60 * 60,
        DAILY_ACCUMULATION: 24 * 60 * 60,
        WEEKLY_ACCUMULATION: 168 * 60 * 60,
        MONTHLY_ACCUMULATION: 744 * 60 * 60,
      },
    };
    series.forEach((singleSeries) => {
      const singleSeriesWithMaxValue: AccumulationDataSeriesWithMaxValue = {
        ...singleSeries,
        maxValue: null,
      };
      if (singleSeries.unitName.toUpperCase() in maxValueMap) {
        const unitSelection = maxValueMap[singleSeries.unitName.toUpperCase() as "HOURS" | "MINUTES" | "SECONDS"];
        if (accumulationType in unitSelection) {
          singleSeriesWithMaxValue.maxValue = unitSelection[accumulationType];
        }
      }
      seriesWithMaxValues.push(singleSeriesWithMaxValue);
    });
    return seriesWithMaxValues;
  }

  // Calculate max y-axis border value.
  function verticalBorderMax(series: AccumulationDataSeriesWithMaxValue[]): number {
    let maxValue: number | null = null;

    // Find the max value.
    series.forEach((singleSeries) => {
      // Check if the current series has a fixed max value. If it does, then we don't need to calculate a dynamic max.
      if (singleSeries.maxValue === null) {
        singleSeries.data.forEach((dataPoint) => {
          let value = dataPoint;
          if (typeof value === "string") {
            value = parseFloat(value);
          }
          if (maxValue === null || value > maxValue) {
            maxValue = value;
          }
        });
      } else {
        if (maxValue === null || singleSeries.maxValue > maxValue) {
          maxValue = singleSeries.maxValue;
        }
      }
    });

    // Check to see if all series have a fixed max value, if they do, then don't add any buffer since there is
    // an explicitly set max buffer for the y-axis.
    const allSeriesHaveFixedMaxValue = series.every((singleSeries) => singleSeries.maxValue !== null);

    // Add a buffer to the max value if not all series are using a fixed max value.
    if (maxValue === null) {
      maxValue = 0;
    } else if (maxValue >= BARELY_BELOW_ZERO_NUMBER && maxValue < BARELY_ABOVE_ZERO_NUMBER) {
      maxValue = 0.25;
    } else if (!allSeriesHaveFixedMaxValue) {
      maxValue = parseFloat(
        ((maxValue as number) + Math.abs(maxValue) * 0.25).toFixed(GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO)
      );
    }

    return maxValue;
  }

  // Calculate min y-axis border value.
  function verticalBorderMin(series: AccumulationDataSeriesWithMaxValue[]): number {
    let minValue: number | null = null;
    // Find the min value.
    series.forEach((singleSeries) => {
      // Check if the current series has a fixed max value. If it does, then we can assume that it has a fixed
      // min value of 0, since all fixed values are for time range accumulation.
      if (singleSeries.maxValue === null) {
        singleSeries.data.forEach((dataPoint) => {
          let value = dataPoint;
          if (typeof value === "string") {
            value = parseFloat(value as string);
          }
          if (minValue === null || value < minValue) {
            minValue = value;
          }
        });
      } else {
        if (minValue === null || 0 < minValue) {
          minValue = 0;
        }
      }
    });

    // If our minValue is non-negative, then just assume the minimum for accumulation is 0. Otherwise add a buffer.
    if (minValue === null || minValue >= 0) {
      minValue = 0;
    } else if (minValue < 0 && minValue > BARELY_BELOW_ZERO_NUMBER) {
      minValue = -0.25;
    } else {
      const offset = parseFloat(
        (minValue - Math.abs(minValue) * 0.25).toFixed(GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO)
      );
      minValue = offset;
    }

    return minValue;
  }

  return options ? (
    <div className="global-style-override">
      <Chart options={options} series={series} type="bar" height={props.height} />
    </div>
  ) : null;
}

interface Props {
  startDate: string;
  endDate: string;
  series: VariousTimeDataSeries[];
  axisMode: AxisMode;
  accumulationType: AccumulationType;
  height: number | string;
}

interface VariousTimeDataSeries {
  name: string;
  unitName: string;
  unitShortName: string;
  data: [number, number | string][];
  dataMarkerMap?: DataMarkerMap;
}

interface DataMarkerMap {
  [key: string]: DataMarker;
}

interface DataMarker {
  value: number | string;
}

interface AccumulationDataSeries {
  name: string;
  unitName: string;
  unitShortName: string;
  data: number[];
}

interface AccumulationDataSeriesWithMaxValue {
  name: string;
  unitName: string;
  unitShortName: string;
  data: number[];
  maxValue: number | null;
}

type AccumulationType = "HOURLY_ACCUMULATION" | "DAILY_ACCUMULATION" | "WEEKLY_ACCUMULATION" | "MONTHLY_ACCUMULATION";

type AxisMode = "Y_AXIS_ATTRIBUTE" | "Y_AXIS_UOM";
